import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import './MobileNavigation.css';

function plainText(value) {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return React.Children.toArray(value).map(child => React.isValidElement(child) ? plainText(child.props.children) : plainText(child)).join(' ');
}
// Consume the same role-filtered elements as the desktop menu; never rebuild permissions here.
export function navigationEntries(children, parents = []) {
  return React.Children.toArray(children).flatMap(child => {
    if (!React.isValidElement(child)) return [];
    const p = child.props;
    if (p.path && p.title) {
      const title = plainText(p.title).trim();
      return [{ path: p.path, title, display: p.title, onClick: p.onClick, parents }];
    }
    return navigationEntries(p.children, p.title ? [...parents, plainText(p.title)] : parents);
  });
}
const groups = ['Sales & customers', 'Contracts & operations', 'Inventory', 'Reports', 'Learning & tools', 'Admin'];
function groupFor(entry) {
  const root = entry.parents[0], path = entry.path;
  if (root === 'Admin' || root === 'More') return 'Admin';
  if (root === 'Reports' || path.startsWith('/reports')) return 'Reports';
  if (root === 'Inventory') return 'Inventory';
  if (root === 'CRM' && !path.startsWith('/conversation-bank')) return 'Sales & customers';
  if (root === 'Tools' || root === 'Tools & Resources' || ['/sales-academy', '/training-resources', '/conversation-bank'].includes(path)) return 'Learning & tools';
  return 'Contracts & operations';
}
const friendlyTitle = entry => entry.title === 'Pipeline' ? (entry.parents[0] || '') + ' pipeline' : entry.title === 'Search' ? 'Search contracts' : entry.title;
export default function MobileNavigation({ children, pathname, onClose, onLogout, onProfile, onPageVideo, onAppVideo, name, expanded }) {
  const [query, setQuery] = useState(''), [openGroup, setOpenGroup] = useState('');
  useEffect(() => { if (!expanded) { setQuery(''); setOpenGroup(''); } }, [expanded]);
  const unique = new Map();
  navigationEntries(children).forEach(entry => { if (!unique.has(entry.path)) unique.set(entry.path, entry); });
  const entries = [...unique.values()], q = query.trim().toLowerCase();
  const matches = entries.filter(entry => [friendlyTitle(entry), ...entry.parents, groupFor(entry)].join(' ').toLowerCase().includes(q));
  const quick = ['/top-producer-view', '/crm/calendar', '/pipeline', '/inventory/browse', '/dochub/print-groups', '/service-requests/browse'].map(path => unique.get(path)).filter(Boolean).slice(0, 6);
  function link(entry, showGroup = false) {
    return <a key={entry.path} className={'mobile-page-link ' + (pathname === entry.path ? 'active' : '')} href={entry.path === '#' ? undefined : entry.path}
      role={entry.path === '#' ? 'button' : undefined} tabIndex={0} aria-current={pathname === entry.path ? 'page' : undefined}
      onKeyDown={event => { if (entry.path === '#' && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); event.currentTarget.click(); } }}
      onClick={event => { if (entry.onClick) { event.preventDefault(); entry.onClick(event); } onClose(); }}>
      <span>{entry.title === 'Pipeline' || entry.title === 'Search' ? friendlyTitle(entry) : entry.display}</span>
      {showGroup && <small>{entry.parents.join(' · ') || groupFor(entry)}</small>}
    </a>;
  }
  return <div className="mobile-navigation d-lg-none">
    <div className="mobile-menu-controls">
      <div className="d-flex justify-content-between align-items-center mb-2"><strong>Menu</strong><Button variant="outline-secondary" size="sm" onClick={onClose}>Close menu</Button></div>
      <Form.Control aria-label="Find a page" type="search" placeholder="Find a page…" value={query} onChange={event => setQuery(event.target.value)}/>
    </div>
    <div className="mobile-menu-scroll">
      {q ? <nav aria-label="Page search results">{matches.length ? matches.map(entry => link(entry, true)) : <p className="p-3">No matching pages.</p>}</nav> : <>
        {quick.length > 0 && <nav className="mobile-quick-links" aria-label="Everyday pages">{quick.map(entry => link(entry))}</nav>}
        <nav aria-label="All CRM pages">{groups.map(group => {
          const items = entries.filter(entry => groupFor(entry) === group);
          if (!items.length) return null;
          const open = openGroup === group, id = 'mobile-group-' + groups.indexOf(group);
          return <section className="mobile-menu-group" key={group}><button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpenGroup(open ? '' : group)}>{group}<span aria-hidden="true">{open ? '−' : '+'}</span></button>
            <div id={id} hidden={!open}>{items.map(entry => link(entry, entry.parents.length > 1))}</div></section>;
        })}</nav>
      </>}
      <div className="mobile-account-actions" aria-label="Account and help">
        {onPageVideo && <Button variant="outline-secondary" onClick={() => { onClose(); onPageVideo(); }}>Help for this page</Button>}
        {onAppVideo && <Button variant="outline-secondary" onClick={() => { onClose(); onAppVideo(); }}>About this app</Button>}
        <Button variant="outline-primary" onClick={() => { onClose(); onProfile(); }}>{name || 'My'} profile</Button>
        <Button variant="outline-secondary" onClick={() => { onClose(); onLogout(); }}>Sign out</Button>
      </div>
    </div>
  </div>;
}
